/**
 * Pure helpers used by useSessionKeepAlive to decide when to refresh
 * a Supabase session and how to react to refresh errors.
 *
 * Kept side-effect-free so they can be unit tested in a node environment
 * without DOM, fake timers, or a Supabase client.
 */

export interface SessionLike {
  expires_at?: number | null;
}

export const DEFAULT_REFRESH_BUFFER_MS = 60_000;

/**
 * Decide whether the current session needs a refresh now.
 *
 * Returns true when the session expires within `bufferMs` (or is already
 * expired). When `session` is null, no refresh is needed — there is no
 * active session to keep alive.
 *
 * `expires_at` from Supabase is unix seconds.
 */
export function shouldRefreshSession(
  session: SessionLike | null,
  nowMs: number,
  bufferMs: number = DEFAULT_REFRESH_BUFFER_MS,
): boolean {
  if (!session) return false;
  const expiresAtMs = (session.expires_at ?? 0) * 1000;
  return expiresAtMs - nowMs <= bufferMs;
}

const FATAL_REFRESH_PATTERNS = [
  "refresh_token_not_found",
  "invalid_grant",
  "Invalid Refresh Token",
] as const;

/**
 * Classify a Supabase refreshSession error message.
 *
 * "fatal" means the refresh token itself is no longer usable; the caller
 * should send the user to /login. "retryable" covers transient network
 * issues, where another visibility/online tick will retry naturally.
 */
export function classifyRefreshError(
  message: string | null | undefined,
): "fatal" | "retryable" {
  if (!message) return "retryable";
  return FATAL_REFRESH_PATTERNS.some((pattern) => message.includes(pattern))
    ? "fatal"
    : "retryable";
}
